// @flow
import { List, Record, Map as ImmutableMap } from 'immutable';
import type { RecordFactory, RecordOf } from 'immutable';

import { getLogger } from 'utils/logger';
import { isQuicken } from 'isAcme';

import type { QcsSyncResourceStoreProps } from 'data/qcsSyncResourceTypes';

const log = getLogger('data/accounts/types');


// ////////////////////////////////////////////////////////////////////////////////////////////////
// Account - Base Account Properties
// ////////////////////////////////////////////////////////////////////////////////////////////////

type AccountProps = {
  type: string,
  id: string,
  clientId: ?string,
  createdAt: ?string,

  institutionLoginId: ?string,

  name: string,
  subType: ?string,
  usageType: ?string,

  description: ?string,
  notes: ?string,

  isExcludedFromAccountBar: ?boolean,
  isExcludedFromBudgets: ?boolean,
  isExcludedFromReports: ?boolean,

  onlineBalance: ?number,
  onlineBalanceAt: ?string,

  currency: ?string,

  isClosed: ?boolean,
  closedOn: ?string,

  isDeleted: ?boolean,
  isIgnored: ?boolean,
  isNotSynced: ?boolean,

  modifiedAt: ?number,
  balanceAsOf: ?number,
  balanceAsOfOn: ?string,
  recurringTxn: ?Object,

  currentBalanceAsOf: ?number,
  currentBalanceAsOfOn: ?string,

  aggregators: ?List<*>,

  // this is web redux data only, not shared with QCS (ignored in transformers)
  topDownBalance: ?number,

}
export type Account = RecordOf<AccountProps>;


// ***** WARNING ***** READ before setting any defaults below
//
// NOTE, if you provide any default account props here, you need to explicitly remove them in
// mkUpdateAccount below, to prevent overwriting these values when someone is trying to update only
// specific fields in an account
// *****
const defaultAccountProps = ({
  id: undefined,
  clientId: undefined,
  createdAt: undefined,

  institutionLoginId: undefined,

  name: undefined,
  subType: undefined,
  usageType: undefined,

  description: undefined,
  notes: undefined,

  isExcludedFromAccountBar: undefined,
  isExcludedFromBudgets: undefined,
  isExcludedFromReports: undefined,

  onlineBalance: undefined,
  onlineBalanceAt: undefined,

  currency: 'USD',

  isClosed: undefined,
  closedOn: undefined,

  isDeleted: undefined,
  isIgnored: undefined,
  isNotSynced: undefined,

  modifiedAt: undefined,
  balanceAsOf: undefined,
  balanceAsOfOn: undefined,
  currentBalanceAsOf: undefined,
  currentBalanceAsOfOn: undefined,

  recurringTxn: { value: 0, unitType: 'DAYS'},

  aggregators: undefined,
});

type AccountAggregatorProps = {
  id: ?string,
  cpId: ?string,
  channel: ?string,

  subType: ?string,

  accountName: ?string,
  accountNumberMasked: ?string,

  aggStatus: ?string,
  aggStatusCode: ?string,
  aggStatusDetail: ?string,

  lastRefreshAttemptedAt: ?string,
  lastRefreshSuccessfulAt: ?string,
}
export type AccountAggregator = RecordOf<AccountAggregatorProps>;
export const mkAccountAggregator: RecordFactory<AccountAggregatorProps> =
  Record({
    id: undefined,
    cpId: undefined,
    channel: undefined,

    subType: undefined,

    accountName: undefined,
    accountNumberMasked: undefined,

    aggStatus: undefined,
    aggStatusCode: undefined,
    aggStatusDetail: undefined,

    lastRefreshAttemptedAt: undefined,
    lastRefreshSuccessfulAt: undefined,
  });

// ================================================================================================
// BankAccount - Bank Account Properties & Types
// ================================================================================================

type BankAccountProps = AccountProps & {
  interestRate: ?string,
  maturityDateOn: ?string,
  minimumNoFeeBalance: ?string,
  monthlyFee: ?string,
  freeBillPay: ?string,
  atmFeeReimbursement: ?string,
}
export type BankAccount = RecordOf<BankAccountProps>;

export const mkBankAccount: RecordFactory<BankAccountProps> =
  Record({
    ...defaultAccountProps,
    type: 'BANK',
    subType: undefined,
    interestRate: undefined,
    maturityDateOn: undefined,
    minimumNoFeeBalance: undefined,
    monthlyFee: undefined,
    freeBillPay: undefined,
    atmFeeReimbursement: undefined,
  });

export const mkCashAccount: RecordFactory<BankAccountProps> =
  Record({
    ...defaultAccountProps,
    type: 'CASH',
    subType: undefined,
  });


// ================================================================================================
// CreditAccount - Credit Account Properties & Types
// ================================================================================================

type CreditAccountProps = AccountProps & {
  cardType: ?string,
  minPayment: ?string,
  cashInterestRate: ?string,
  creditLimit: ?string,
  rewardsType: ?string,
  annualFee: ?number,
  statementDueDate: ?number,
  statementDueAmount: ?string,
  statementMinPayment: ?string,
}
export type CreditAccount = RecordOf<CreditAccountProps>;

export const mkCreditAccount: RecordFactory<CreditAccountProps> =
  Record({
    ...defaultAccountProps,
    type: 'CREDIT',
    subType: undefined,
    cardType: undefined,
    minPayment: undefined,
    cashInterestRate: undefined,
    creditLimit: undefined,
    rewardsType: undefined,
    annualFee: undefined,
    statementDueDate: undefined,
    statementDueAmount: undefined,
    statementMinPayment: undefined,
  });

// ================================================================================================
// GoalAccount - Goal Account Properties & Types
// ================================================================================================

type GoalAccountProps = AccountProps & {

}
export type GoalAccount = RecordOf<GoalAccountProps>;

export const mkGoalAccount: RecordFactory<GoalAccountProps> =
  Record({
    ...defaultAccountProps,
    type: 'GOAL',
    subType: null,
  });


// ================================================================================================
// LoanAccount - Loan Account Properties & Types
// ================================================================================================

type LoanAccountProps = AccountProps & {
  cardType: ?string,
  minPayment: ?string,
  cashInterestRate: ?string,
  creditLimit: ?string,
  rewardsType: ?string,
  annualFee: ?number,
  statementDueDate: ?number,
  statementDueAmount: ?string,
  statementMinPayment: ?string,
}
export type LoanAccount = RecordOf<LoanAccountProps>;

export const mkLoanAccount: RecordFactory<LoanAccountProps> =
  Record({
    ...defaultAccountProps,
    type: 'LOAN',
    subType: undefined,
    cardType: undefined,
    minPayment: undefined,
    cashInterestRate: undefined,
    creditLimit: undefined,
    rewardsType: undefined,
    annualFee: undefined,
    statementDueDate: undefined,
    statementDueAmount: undefined,
    statementMinPayment: undefined,
  });

// ================================================================================================
// OtherAssertAccount - Other Asset Properties & Types
// ================================================================================================

type OtherAssetAccountProps = AccountProps & {

}
export type OtherAssetAccount = RecordOf<OtherAssetAccountProps>;

export const mkOtherAssetAccount: RecordFactory<OtherAssetAccountProps> =
  Record({
    ...defaultAccountProps,
    type: 'OTHER_ASSET',
    subType: undefined,
  });

// ================================================================================================
// OtherLiabilityAccount - Other Account Properties & Types
// ================================================================================================

type OtherLiabilityAccountProps = AccountProps & {

}
export type OtherLiabilityAccount = RecordOf<OtherLiabilityAccountProps>;

export const mkOtherLiabilityAccount: RecordFactory<OtherLiabilityAccountProps> =
  Record({
    ...defaultAccountProps,
    type: 'OTHER_LIABILITY',
    subType: undefined,
  });

// ================================================================================================
// InvestmentAccount - Investment Account Properties & Types
// ================================================================================================

type InvestmentAccountProps = AccountProps & {
  cashBalance: ?number,
}
export type InvestmentAccount = RecordOf<InvestmentAccountProps>;

export const mkInvestmentAccount: RecordFactory<InvestmentAccountProps> =
  Record({
    ...defaultAccountProps,
    type: 'INVESTMENT',
    subType: undefined,
    usageType: undefined,
    cashBalance: undefined,
  });

// ================================================================================================
// RealEstateAccount - Real Estate Properties & Types
// ================================================================================================

type RealEstateAccountProps = AccountProps & {
  line1: ?string,
  line2: ?string,
  city: ?string,
  state: ?string,
  zipCode: ?string,
}
export type RealEstateAccount = RecordOf<RealEstateAccountProps>;

export const mkRealEstateAccount: RecordFactory<RealEstateAccountProps> =
  Record({
    ...defaultAccountProps,
    type: 'REAL_ESTATE',
    subType: undefined,
    line1: undefined,
    line2: undefined,
    city: undefined,
    state: undefined,
    country: undefined,
    zipCode: undefined,
  });


// ================================================================================================
// VehicleAccount - Vehicle Properties & Types
// ================================================================================================

type VehicleAccountProps = AccountProps & {
  year: ?number,
  make: ?string,
  model: ?string,
  trim: ?string,
  mileage: ?number,
}
export type VehicleAccount = RecordOf<VehicleAccountProps>;

export const mkVehicleAccount: RecordFactory<VehicleAccountProps> =
  Record({
    ...defaultAccountProps,
    type: 'REAL_ESTATE',
    subType: undefined,

    year: undefined,
    make: undefined,
    model: undefined,
    trim: undefined,
    mileage: undefined,
  });

// ================================================================================================
// UnknownAccount - Unknown Account Properties & Types
// ================================================================================================

type UnknownAccountProps = AccountProps & {

}
export type UnknownAccount = RecordOf<UnknownAccountProps>;

export const mkUnknownAccount: RecordFactory<UnknownAccountProps> =
  Record({
    ...defaultAccountProps,
    type: 'UNKNOWN',
    subType: undefined,
  });

// ================================================================================================
// AccountRecordTypes - Union of All Account Record Types
// ================================================================================================

export type AccountRecordTypes =
  BankAccount |
  CreditAccount |
  GoalAccount |
  LoanAccount |
  InvestmentAccount |
  OtherAssetAccount |
  OtherLiabilityAccount |
  RealEstateAccount |
  VehicleAccount |
  UnknownAccount;

export const mkAccount = (type : string, options: any) => {

  switch (type) {
    case 'CASH':
      return mkCashAccount(options);
    case 'BANK':
      return mkBankAccount(options);
    case 'CREDIT':
      return mkCreditAccount(options);
    case 'GOAL':
      return mkGoalAccount(options);
    case 'INVESTMENT':
      return mkInvestmentAccount(options);
    case 'LOAN':
      return mkLoanAccount(options);
    case 'OTHER_ASSET':
      return mkOtherAssetAccount(options);
    case 'OTHER_LIABILITY':
      return mkOtherLiabilityAccount(options);
    case 'REAL_ESTATE':
      return mkRealEstateAccount(options);
    case 'VEHICLE':
      return mkVehicleAccount(options);
    case 'UNKNOWN':
      return mkUnknownAccount(options);
    default:
      log.warn('Was a new Account Type Added? Need to update mkAccountForType');
      return mkBankAccount(options);
  }
};

// ================================================================================================
// Call to get an empty account "template" to make account updates with (based on type)
// ensure all fields are null as to not accidently overwrite anything
// ================================================================================================
export const mkUpdateAccount = (type : string, options : any) =>
  mkAccount(type, {
    currency: undefined,
    recurringTxn: undefined,
    id: undefined,
    subType: undefined,
    ...options,
  });

// ================================================================================================
// AccountsStore - Accounts Store Properties & Types
// ================================================================================================

type AccountsStoreProps = QcsSyncResourceStoreProps & {
  isRefreshing: boolean,  // true when accounts are refreshing on server
  loadPendingInvestments: boolean,
};
export type AccountsStore = RecordOf<AccountsStoreProps>;

export const makeAccountStore: RecordFactory<AccountsStoreProps> =
  Record({
    resourcesById: ImmutableMap(),
    lastSyncDate: undefined,

    resourceName: 'ACCOUNTS',
    loadPending: true,
    isLoading: false,
    loadPendingInvestments: true,
    isLoadingInvestments: false,

    isRefreshing: false,
  });

// ================================================================================================
// RefreshResponse -
// ================================================================================================

export type RefreshAccountsCompletedReason =
  'DONE_PROCESSING | NO_ACCOUNTS_TO_REFRESH | FAILED_WHEN_SUBMITTING | FAILED_WHEN_POLLING | TIMEOUT_WHILE_PROCESSING';

type RefreshAggregatorProps = {
  channel: ?string,

  isProcessing: boolean,
  aggStatus: ?string,
  cpAggStatus: ?string,
  cpAggStatusDetail: ?string,

  mfaChallenges: ?any,
}
export type RefreshAggregator = RecordOf<RefreshAggregatorProps>;
export const makeRefreshAggregator: RecordFactory<RefreshAggregatorProps> =
  new Record({
    channel: undefined,

    isProcessing: false,
    aggStatus: undefined,
    cpAggStatus: undefined,
    cpAggStatusDetail: undefined,

    mfaChallenges: undefined,
    accounts: undefined,
  });

type RefreshInstitutionLoginProps = {
  id: string,

  isProcessing: boolean,
  status: string,

  credentialsBlob: ?string,
  submitUrl: ?string,

  aggregators: ?List<RefreshAggregator>,
}
export type RefreshInstitutionLogin = RecordOf<RefreshInstitutionLoginProps>;
export const makeRefreshInstitutionLogin: RecordFactory<RefreshInstitutionLoginProps> =
  new Record({
    id: '0',

    isProcessing: false,
    status: 'OK',

    credentialsBlob: undefined,
    submitUrl: undefined,

    aggregators: undefined,
  });

type RefreshResponseProps = {
  jobId: string,

  isProcessing: boolean,
  status: string,

  institutionLogins: List<RefreshInstitutionLogin>
}
export type RefreshResponse = RecordOf<RefreshResponseProps>;
export const makeRefreshResponse: RecordFactory<RefreshResponseProps> =
  new Record({
    jobId: '0',

    isProcessing: false,
    status: 'OK',

    institutionLogins: List(),
  });


// ================================================================================================
// AccountTypes
// ================================================================================================

export type AccountTypeNodeProps = {
  id: string,
  displayName: string,
  qcsType: string,
  qcsSubType: ?string,

  selectable: boolean,
  connectable: boolean,

  other: boolean,
  children: ?List<AccountTypeNode>,
};
export type AccountTypeNode = RecordOf<AccountTypeNodeProps>;
export const mkAccountTypeNode: RecordFactory<AccountTypeNodeProps> =
  new Record({
    id: 'BANKING',
    displayName: 'BANKING',

    qcsType: 'BANKING',
    qcsSubType: undefined,

    selectable: true,
    connectable: true,

    other: false,

    children: undefined,
  });

/* eslint-disable */
const AccountTypeData = [
  // PARENT_ID    ID                  DISPLAY_NAME           QCS_TYPE           QCS_SUBTYPE          SELECTABLE  CONNECTABLE  OTHER
  [ '0',          'BANK',             'Banking',             'BANK',             undefined,          true,       true,        false],
  [ 'BANK',       'CHECKING',         'Checking',            'BANK',            'CHECKING',          true,       true,        false],
  [ 'BANK',       'SAVINGS',          'Savings',             'BANK',            'SAVINGS',           true,       true,        false],
  [ 'BANK',       'CD',               'CD',                  'BANK',            'CD',                true,       true,        false],
  [ 'BANK',       'MONEY_MARKET',     'Money Market',        'BANK',            'MONEY_MARKET',      true,       true,        false],
  [ 'BANK',       'OTHER_BANKING',    'Other Banking',       'BANK',            'OTHER_BANK',        true,       true,        true],
  [ '0',          'CASH',             'Cash',                'CASH',             undefined,          isQuicken,  false,       false],
  [ '0',          'CREDIT',           'Credit',              'CREDIT',           undefined,          true,       true,        false],
  [ 'CREDIT',     'CREDIT_CARD',      'Credit Card',         'CREDIT',          'CREDIT_CARD',       true,       true,        false],
  [ 'CREDIT',     'LINE_OF_CREDIT',   'Line of Credit',      'CREDIT',          'LINE_OF_CREDIT',    true,       true,        false],
  [ 'CREDIT',     'OTHER_CREDIT',     'Other Credit',        'CREDIT',          'OTHER_CREDIT',      true,       true,        true],
  [ '0',          'LOAN',             'Loan',                'LOAN',             undefined,          true,       true,        false],
  [ 'LOAN',       'AUTO',             'Auto Loan',           'LOAN',            'AUTO_LOAN',         true,       true,        false],
  [ 'LOAN',       'CONSUMER',         'Consumer Loan',       'LOAN',            'CONSUMER_LOAN',     true,       true,        false],
  [ 'LOAN',       'CONSTRUCTION',     'Construction Loan',   'LOAN',            'CONSTRUCTION_LOAN', true,       true,        false],
  [ 'LOAN',       'HOME_EQUITY',      'Home Equity Line',    'LOAN',            'HOME_EQUITY',       true,       true,        false],
  [ 'LOAN',       'MILITARY',         'Military Loan',       'LOAN',            'MILITARY_LOAN',     true,       true,        false],
  [ 'LOAN',       'MORTGAGE',         'Mortgage',            'LOAN',            'MORTGAGE',          true,       true,        false],
  [ 'LOAN',       'SMALL_BUSINESS',   'Small Business Loan', 'LOAN',            'SMB',               true,       true,        false],
  [ 'LOAN',       'STUDENT',          'Student Loan',        'LOAN',            'STUDENT_LOAN',      true,       true,        false],
  [ 'LOAN',       'OTHER_LOAN',       'Other Loan',          'LOAN',            'LOAN',              true,       true,        true],
  [ '0',          'ASSET',            'Asset',               'OTHER_ASSET',      undefined,          true,       false,       false],
  [ 'ASSET',      'REAL_ESTATE',      'Real Estate',         'REAL_ESTATE',      undefined,          true,       false,       false],
  [ 'ASSET',      'VEHICLE',          'Vehicle',             'VEHICLE',          undefined,          true,       false,       false],
  [ 'ASSET',      'OTHER_ASSET',      'Other Asset',         'OTHER_ASSET',     'OTHER_ASSET',       true,       false,       true],
  [ '0',          'LIABILITY',        'Other Liability',     'OTHER_LIABILITY', 'OTHER_LIABILITY',   true,       false,       false],
  [ '0',          'INVESTMENT',       'Investments',         'INVESTMENT',       undefined,          true,       true,        false],
  [ 'INVESTMENT', 'BROKERAGE',        'Brokerage',           'INVESTMENT',      'BROKERAGE',         true,       true,        false],
  [ 'INVESTMENT', 'IRA',              'IRA',                 'INVESTMENT',      'IRA',               true,       true,        false],
  [ 'INVESTMENT', 'KEOGH',            'Keogh',               'INVESTMENT',      'KEOGH',             true,       true,        false],
  [ 'INVESTMENT', 'ROTH_IRA',         'Roth IRA',            'INVESTMENT',      'ROTH_IRA',          true,       true,        false],
  [ 'INVESTMENT', 'ROTH_401K',        'Roth 401K',           'INVESTMENT',      'ROTH_401K',         true,       true,        false],
  [ 'INVESTMENT', 'SEP_IRA',          'SEP IRA',             'INVESTMENT',      'SEP_IRA',           true,       true,        false],
  [ 'INVESTMENT', 'SIMPLE_IRA',       'Simple IRA',          'INVESTMENT',      'SIMPLE_IRA',        true,       true,        false],
  [ 'INVESTMENT', 'UGMA_UTMA',        'UGMA/UTMA',           'INVESTMENT',      'UGMA_UTMA',         true,       true,        false],
  [ 'INVESTMENT', '401K',             '401K',                'INVESTMENT',      '401K',              true,       true,        false],
  [ 'INVESTMENT', '403B',             '403B',                'INVESTMENT',      '403B',              true,       true,        false],
  [ 'INVESTMENT', '529_PLAN',         '529 Plan',            'INVESTMENT',      '529',               true,       true,        false],
  [ 'INVESTMENT', 'OTHER_INVESTMENT', 'Other Investment',    'INVESTMENT',      'OTHER_INVESTMENTS', true,       true,        true],
  [ '0',          'UNKNOWN',          'Unknown',             'UNKNOWN',         'UNKNOWN',           false,      false,       false],
];
/* eslint-enable */

const AccountTypeDataById = AccountTypeData.reduce(
  (map, data) => {
    const parentId = data[0] || '0';
    map.set(parentId, map.get(parentId) || []);

    const parent = map.get(parentId);
    if (parent) {
      parent.push({
        id: data[1],
        displayName: data[2],
        qcsType: data[3],
        qcsSubType: data[4],
        selectable: data[5],
        connectable: data[6],
        other: data[7],
      });
    }

    return map;
  }, new Map()
);

const createAccountTypeNode = (accountTypesByParentId, data, children) => {
  const childNodes = children ? List(
    children.map((child) => {
      const innerChildren = accountTypesByParentId.get(child.id);
      return createAccountTypeNode(accountTypesByParentId, child, innerChildren);
    })
  ) : null;
  return mkAccountTypeNode({
    id: data.id,
    displayName: data.displayName,
    qcsType: data.qcsType,

    qcsSubType: data.qcsSubType,

    selectable: data.selectable,
    connectable: data.connectable,

    other: data.other,

    children: childNodes,
  });
};

export const AccountTypeNodesTree = List(
  // $FlowFixMe
  AccountTypeDataById.get('0').map((data) => {
    const children = AccountTypeDataById.get(data.id);
    return createAccountTypeNode(AccountTypeDataById, data, children);
  })
);

const addAccountTypeNodeToIdMap = (map, node) => {
  map.set(node.id, node);
  if (node.children) {
    node.children.forEach((child) => addAccountTypeNodeToIdMap(map, child));
  }
};
const AccountTypeNodesById = ImmutableMap().withMutations((map) => {
  AccountTypeNodesTree.forEach((node) => addAccountTypeNodeToIdMap(map, node));
});

function buildAccountTypeNodesByQcsType(accountTypeNodesById) {
  const subTypesByType = new Map();
  accountTypeNodesById.forEach((node) => {
    subTypesByType.set(node.qcsType, subTypesByType.get(node.qcsType) || []);

    const subTypes = subTypesByType.get(node.qcsType);
    if (subTypes) {
      subTypes.push([node.qcsSubType ? node.qcsSubType : 'null', node]);
    }
  });

  const nodesByQcsType = ImmutableMap().withMutations((map) => {
    subTypesByType.forEach((value, key) => map.set(key, ImmutableMap(value)));
  });

  return nodesByQcsType;
}
const AccountTypeNodesByQcsType = buildAccountTypeNodesByQcsType(AccountTypeNodesById);

export const getAccountTypeNodeByQcsType = (qcsType: string, qcsSubType: ?string) => {
  const subTypesOfNode = AccountTypeNodesByQcsType.get(qcsType);
  if (!subTypesOfNode) {
    log.warn("Unknown account type", qcsType);
    const node = AccountTypeNodesById.get('UNKNOWN');
    if (node == null) {
      throw new Error('Expected to find an account type node for UNKNOWN types');
    }
    return node;
  }
  let node = null;
  if (qcsSubType) {
    // get exact node that matches QCS
    node = subTypesOfNode.get(qcsSubType);
  }
  if (!node) {
    // if we cannot find subtype, then map to the other category belonging to same parent
    node = subTypesOfNode.find((value) => value.other);
  }
  if (!node) {
    // if we still don't have node, try to use parent node (this may have issues with Select menus!)
    node = subTypesOfNode.get('null');
  }
  if (!node) {
    node = AccountTypeNodesById.get('UNKNOWN');
    if (node == null) {
      throw new Error('Expected to find an account type node for UNKNOWN types');
    }
  }
  return node;
};
